import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import { FaChevronLeft, FaChevronRight, FaArrowLeft } from "react-icons/fa";
import { availabilityAPI, bookingAPI, nurseAPI } from "../services/api";
import { tokenManager } from "../services/tokenManager";
import { toast } from 'react-toastify';
import { paymentUtils } from '../utils/paymentUtils';
import CommunicationModePopup from "../components/CommunicationModePopup";




function SlotsSkeleton() {
  return (
    <div className="max-h-96 overflow-y-auto animate-pulse">
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            {[...Array(7)].map((_, i) => (
              <th key={i} className="px-2 py-2 text-center">
                <div className="h-4 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                <div className="h-5 bg-gray-200 rounded w-6 mx-auto"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, rowIdx) => (
            <tr key={rowIdx}>
              {[...Array(7)].map((_, colIdx) => (
                <td key={colIdx} className="px-2 py-2 text-center">
                  <div className="h-6 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NurseDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [days, setDays] = useState([]);
  // const [currentWeek, setCurrentWeek] = useState(moment().startOf("week"));
  const [currentWeek, setCurrentWeek] = useState(moment("2025-09-15"));

  const [availableSlots, setAvailableSlots] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(null);
  
  // Communication mode popup state
  const [showCommunicationModePopup, setShowCommunicationModePopup] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // Get rescheduling data from location state
  const isRescheduling = location.state?.isRescheduling;
  const sessionToReschedule = location.state?.sessionToReschedule;

  // If rescheduling, set initial week to the session's week
  useEffect(() => {
    if (isRescheduling && sessionToReschedule?.date) {
      setCurrentWeek(moment(sessionToReschedule.date).startOf("week"));
    }
  }, [isRescheduling, sessionToReschedule]);

  useEffect(() => {
    debugger
    const weekStart = currentWeek;
    const weekDays = [];
    for (let i = 0; i <= 6; i++) {
      weekDays.push(moment(weekStart).add(i, "days"));
    }
    setDays(weekDays);
  }, [currentWeek]);

  useEffect(() => {
    const fetchWeekSlots = async () => {
      setSlotsLoading(true);
      setSlotsError(null);
      setAvailableSlots({});
      try {
        if (days.length && id) {
          const dateFrom = days[0].format("YYYY-MM-DD");
          const dateTo = days[6].format("YYYY-MM-DD");
          const response = await availabilityAPI.getAvailableSlotsForRange(id, dateFrom, dateTo);
          setAvailableSlots(response.slotsByDate || {});
        }
      } catch (err) {
        setSlotsError("Failed to fetch available slots. Please try again.");
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchWeekSlots();
  }, [days, id]);

  const handlePrevWeek = () => {
    const minDate = moment('2025-09-15');
    const newWeek = currentWeek.clone().subtract(1, "week");
    
    // Don't allow navigation to weeks before September 15th, 2025
    if (newWeek.isBefore(minDate, 'week')) {
      return;
    }
    
    setCurrentWeek(newWeek);
  };

  const handleNextWeek = () => {
    setCurrentWeek(currentWeek.clone().add(1, "week"));
  };

  const isDateBeforeToday = (date) => date.isBefore(moment(), "day");

  const buildWeekSlotsGrid = (days, slotsByDate) => {
    const allTimes = new Set();
    const filteredSlotsByDate = {};
    const now = moment();
    
    days.forEach(day => {
      const dateStr = day.format('YYYY-MM-DD');
      
      if (!day.isBefore(now, 'day')) {
        const daySlots = slotsByDate[dateStr] || [];
        // Keep original slot times
        filteredSlotsByDate[dateStr] = daySlots;
        daySlots.forEach(slot => {
          allTimes.add(slot.start);
        });
      } else {
        filteredSlotsByDate[dateStr] = [];
      }
    });

    // Sort times and split into AM/PM
    const sortedTimes = Array.from(allTimes).sort((a, b) => {
      const timeA = moment(a, 'h:mm A');
      const timeB = moment(b, 'h:mm A');
      return timeA.diff(timeB);
    });

    const amTimes = sortedTimes.filter(time => moment(time, 'h:mm A').hour() < 12);
    const pmTimes = sortedTimes.filter(time => moment(time, 'h:mm A').hour() >= 12);

    return { 
      sortedTimes: [...amTimes, ...pmTimes], 
      slotsByDate: filteredSlotsByDate 
    };
  };

  const handleTimeSelect = async (date, start, end) => {

    const isPaymentCompleted = paymentUtils.isPaymentCompleted();
    
      if (!isPaymentCompleted) {
        // Redirect to payment page if payment is not completed
        navigate("/care-plan");
        return
      }
    
    if (isDateBeforeToday(moment(date))) return;
    const user = tokenManager.getUser();
    if (!user) return navigate('/login');
    setSelectedTimeSlot({ date, start, end });
    setShowCommunicationModePopup(true);
  };

  const handleCommunicationModeConfirm = async (sessionMode) => {
    if (!selectedTimeSlot) return;
    
    const { date, start, end } = selectedTimeSlot;
    const user = tokenManager.getUser();
    
    try {
      setBookingLoading(date + start);
      
      // Get the exact slot times
      const slot = weekSlots[date].find(s => s.start === start);
      if (!slot) {
        throw new Error('Selected time slot not found');
      }

      // Enforce 20-minute duration
      const startMoment = moment(slot.start, 'h:mm A');
      const endMoment = moment(startMoment).add(20, 'minutes');
      const endTime = endMoment.format('h:mm A');

      if (isRescheduling && sessionToReschedule) {
        // Update existing session with new time slot
        try {
          await nurseAPI.updateSessionTimeSlot(sessionToReschedule.id, {
            startTime: slot.start,
            endTime: endTime,
            date: date
          });

          // Clear session_payment_status after successful reschedule
          paymentUtils.clearPaymentStatus();

          toast.success('Session rescheduled successfully');
          navigate('/dashboard');
        } catch (error) {
          console.error('Error rescheduling session:', error);
          toast.error(error.response?.data?.error || 'Failed to reschedule session');
        }
      } else {
        // Regular booking flow
        const bookingData = {
          nurseId: id,
          patientId: user.id || user._id,
          date,
          startTime: slot.start,
          endTime: endTime,
          sessionType: 'monthly_checkin',
          sessionMode: sessionMode,
          durationMinutes: 20
        };

        const response = await bookingAPI.bookSlot(bookingData);
        if (response?.session?.id) {
          const updatedSlots = { ...availableSlots };
          updatedSlots[date] = updatedSlots[date].filter(s => s.start !== start);
          setAvailableSlots(updatedSlots);
          navigate(`/success?session_id=${response.session.id}`);
        } else {
          toast.error('Booking failed: Invalid response from server');
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to process your request. Please try again.');
    } finally {
      setBookingLoading(null);
    }
  };

  const isTimeSlotPast = (dateStr, timeStr) => {
    const now = moment();
    const today = moment().format('YYYY-MM-DD');
    const isToday = dateStr === today;
    
    // Only check time if it's today
    if (isToday) {
      const slotTime = moment(timeStr, 'h:mm A');
      const currentTime = moment();
      return slotTime.isBefore(currentTime, 'minute');
    }
    
    return false;
  };

  const formatDisplayTime = (time) => {
    // Keep the original time format from the API
    return time;
  };

  const { sortedTimes, slotsByDate: weekSlots } = buildWeekSlotsGrid(days, availableSlots);

  return (
    <div className="h-screen bg-gray-50 overflow-hidden">
      <div className="h-full container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="h-full bg-white p-6 rounded-lg shadow-sm flex flex-col">
          {/* Header */}
          <div className="flex items-center mb-4 gap-5">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <FaArrowLeft className="mr-2" />
              <span></span>
            </button>
            <h2 className="text-xl font-bold text-gray-900">
              {isRescheduling ? 'Reschedule Session' : 'Book a Session'}
            </h2>
          </div>


          {/* {isRescheduling && sessionToReschedule && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-700">
                Current session time: {sessionToReschedule.currentStartTime} - {sessionToReschedule.currentEndTime}
              </p>
              <p className="text-sm text-blue-600">
                Please select a new time slot below
              </p>
            </div>
          )} */}

          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevWeek}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50"
              disabled={days[0]?.isSame(moment(), 'day')}
            >
              <FaChevronLeft className="text-gray-600" />
            </button>
            <span className="text-gray-600 font-medium">
              {days[0]?.format("MMM D")} - {days[6]?.format("MMM D, YYYY")}
            </span>
            <button
              onClick={handleNextWeek}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50"
            >
              <FaChevronRight className="text-gray-600" />
            </button>
          </div>

          {/* Calendar Content */}
          {slotsLoading ? (
            <SlotsSkeleton />
          ) : slotsError ? (
            <div className="text-center text-red-600 py-4">{slotsError}</div>
          ) : (
            <div className="flex-1 min-h-0">
              <div className="h-full overflow-y-auto">
                <table className="min-w-full border-collapse">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr>
                      {days.map((day) => (
                        <th key={day.format("YYYY-MM-DD")} className="px-2 py-2 text-center text-gray-800 bg-white">
                          <div className="text-sm font-medium">{day.format("ddd")}</div>
                          <div className="text-lg font-semibold">{day.format("D")}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTimes.map((time) => {
                      const isAM = moment(time, 'h:mm A').hour() < 12;
                      return (
                        <tr key={time} className='bg-gray-50'>
                          {days.map((day) => {
                            const dateStr = day.format("YYYY-MM-DD");
                            const isPastTime = isTimeSlotPast(dateStr, time);
                            const slots = weekSlots[dateStr] || [];
                            const slot = slots.find((s) => s.start === time);
                            const isLoading = bookingLoading === dateStr + time;
                            
                            return (
                              <td key={dateStr} className="px-2 py-2 text-center">
                                {slot && (
                                  <button
                                    onClick={() => handleTimeSelect(dateStr, slot.start, slot.end)}
                                    disabled={isLoading || isPastTime}
                                    className={`w-full py-1 px-2 text-sm rounded touch-none select-none ${
                                      isLoading
                                        ? "bg-gray-100 text-gray-400"
                                        : isPastTime
                                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                        : "bg-blue-100 text-blue-700 hover:bg-blue-200 active:bg-blue-300"
                                    }`}
                                  >
                                    {isLoading ? "Booking..." : formatDisplayTime(time)}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Communication Mode Popup */}
      <CommunicationModePopup
        isOpen={showCommunicationModePopup}
        onClose={() => setShowCommunicationModePopup(false)}
        onConfirm={handleCommunicationModeConfirm}
        selectedTime={selectedTimeSlot?.start}
        selectedDate={selectedTimeSlot?.date}
      />
    </div>
  );
};

export default NurseDetails;
